import {Button, Header, Pagination, TableSkeleton} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import useLogList from '@/components/Logs/useLogList';
import {
  AfriexTelemetryAction,
  AfriexTelemetryObject,
  AfriexTelemetrySubject,
} from '@/types';
import {getResponseMessage} from '@/utils/errorMessage';

import MerchantLogsFilters from './MerchantLogsFilters';
import MerchantLogsTable from './MerchantLogsTable';

// Scoped to the exact tags ErrorRenderer writes for a failed request (server
// presentation/renderers/ErrorRenderer.ts): SYSTEM/FAILED/USER. Without this
// scope, an empty tags array matches every telemetryService write (admin
// actions, business staff actions, etc.), not just partner request errors.
const ERROR_RESPONSE_TAGS = [
  AfriexTelemetrySubject.System,
  AfriexTelemetryAction.Failed,
  AfriexTelemetryObject.User,
];

const MerchantLogsScreen = () => {
  // The filters (businessId, userId, errorCode, errorMessage, dates) narrow
  // this scope further; they never widen it.
  const {
    error,
    logsList,
    fetchAllLogs,
    isSearch,
    isPageReset,
    isLoading,
    pageInfo,
    filterCount,
    filtersToApply,
    clearFilters,
    selectAppliedFilters,
    handleApplyFilters,
  } = useLogList(ERROR_RESPONSE_TAGS);

  const renderContent = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    // Before the table: a failed request leaves the previous rows in logsList,
    // so rendering them would show a rejected filter as if it had matched.
    if (error) {
      return (
        <div
          role="alert"
          className="mt-5 text-center text-red-500"
          data-cy="merchant-logs-error-state"
        >
          <p className="font-medium">Failed to load merchant logs.</p>
          {/* The fallback covers a failure with no server reason (a network
              failure, say). Most failures here come from a filter, so it
              points there too. */}
          <p>
            {getResponseMessage(error) || 'Try again, or change your filters.'}
          </p>
          <Button
            variant="ghost"
            onClick={() => fetchAllLogs({tags: ERROR_RESPONSE_TAGS})}
            data-cy="merchant-logs-error-retry"
          >
            Retry
          </Button>
        </div>
      );
    }

    return <MerchantLogsTable items={logsList} />;
  };

  return (
    <main className="my-5 mx-4 md:mx-10">
      <Header title="Merchant Logs" dataCy="merchant-logs-title" />

      <MerchantLogsFilters
        filterCount={filterCount}
        filtersToApply={filtersToApply}
        clearFilters={clearFilters}
        selectAppliedFilters={selectAppliedFilters}
        handleApplyFilters={handleApplyFilters}
      />

      <section className="pt-5 md:pt-5">{renderContent()}</section>

      {!error && logsList && logsList.length ? (
        <Pagination
          onPageChange={fetchAllLogs as PaginationProps['onPageChange']}
          total={pageInfo.total}
          isSearch={isSearch}
          isPageReset={isPageReset}
        />
      ) : null}
    </main>
  );
};

export default MerchantLogsScreen;
